import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, TextField, Switch, Typography } from '@mui/material';
import { MuiMarkdown } from 'mui-markdown';
import { Params } from '../types/params';
import createIpynb from '../helpers/create-ipynb';
export const OPENAI = 'openai';
export const MISTRALAI = 'mistralai';
function Generator() {

    const [params, setParams] = useState({} as Params);
    const [responseText, setResponseText] = useState('');
    const [error, setError] = useState('');
    const [notebookName, setNotebookName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [fileHref, setFileHref] = useState('');
    const [provider, setProvider] = useState(OPENAI);
    const [importExport, setImportExport] = useState(false);
    const downloadBtn = useRef();

    const handleParamChange = useCallback((key: string, e: any) => {
        setParams({ ...params, [key]: e.target.value } as Params);
    }, [params, setParams]);

    const handleGenerate = useCallback(async () => {
        if (!params?.domain || !params?.subdomain || !params?.descriptions) {
            setError('Required fields: domain, subdomain, descriptions')
            return
        };


        setIsLoading(true);
        setResponseText('')

        const response = await fetch(provider == OPENAI ? `${process.env.REACT_APP_BASE_URL}/oai/stream` : `${process.env.REACT_APP_BASE_URL}/mai/stream?`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(params)
            }
        );
        setIsLoading(false);

        const reader = response?.body!.getReader();

        const chunks = [];

        let done, value;
        const dec = new TextDecoder()

        while (!done) {
            setIsGenerating(true);
            ({ value, done } = await reader.read());
            if (done) {
                setIsGenerating(false);
                return chunks;
            }
            const strval = dec.decode(value, { stream: true })
            chunks.push(strval);
            setResponseText(prev => prev + strval);
        }

    }, [provider, params]);

    const handleProviderChange = useCallback(() => {
        setProvider(provider == OPENAI ? MISTRALAI : OPENAI)
    }, [provider]);

    function parseUserQuery(str: string) {
        str = str.replaceAll('```json', '').replaceAll('```', '');
        try {
            const obj = JSON.parse(str);

            return Object.values(obj).join('\n');
        }
        catch (error) {
            console.log('error', error)
            return str
        }
    }

    function surroundJSON(str: string) {
        try {
            const obj = JSON.parse(str);
            return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
        } catch (error) {
            str = str.replaceAll('```json', '').replaceAll('```', '');
            try {

                const obj = JSON.parse(str);
                return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
            }
            catch (error) {
                console.log('error', error)
                return str
            }
        }
    }

    const handleImportExport = useCallback((e: any) => {
        setImportExport(x => !x);
    }, []);

    const handleImportExportChange = useCallback((e: any) => {

        let p = {} as Params;
        try {
            p = JSON.parse(e.target.value);
            setParams(p)
        }
        catch (error) {
            console.log('error', error)
        }

    }, []);

    useEffect(() => {

        if (!isGenerating) {

            const responseTextSplitted = responseText.split('----');

            let offset = 0;

            if (responseTextSplitted.length === 5) {
                offset = 1;
            }

            const systemStr = responseTextSplitted[0 + offset] || '{}';
            const toolsStr = responseTextSplitted[1 + offset] || '[]';
            const userStr = responseTextSplitted[2 + offset] || '{}';
            const toolUseStr = responseTextSplitted?.[3 + offset] || '{}';

            const system = '**[system]**\n\n---\n\n## CONTEXT: \n\n' + surroundJSON(systemStr) || 'TODO';
            const tools = '**[tools]**\n\n---\n\n' + surroundJSON(toolsStr) || 'TODO';
            const user = '**[user]**\n\n---\n\n' + parseUserQuery(userStr) || 'TODO';
            const toolUse = '**[tool_use]**\n\n---\n\n' + surroundJSON(toolUseStr) || 'TODO';
            const toolOutput = '**[tool_output]**\n\n---\n\n' + surroundJSON('{"success": true}') || 'TODO';
            const assistant = '**[assistant]**\n\n---\n\n' + 'Great! That\'s successfully done!';

            console.log('----responseText----\n', responseText)

            const notebook = createIpynb({ system, tools, user, toolUse, toolOutput, assistant })

            setFileHref(`data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(notebook))}`)
        }

    }, [isGenerating, responseText]);

    useEffect(() => {
        setNotebookName(`taxonomy,username,000,${params?.domain?.replace(/\s/g, '-').toLowerCase()},${params?.subdomain?.replace(/\s/g, '-').toLowerCase()},behaviour.ipynb`)
    }, [params]);

    return (
        <Stack direction={'column'} gap={5} margin={10}>
            {
                error && <Alert severity='warning'>{error}</Alert>
            }
            <Typography>
                {MISTRALAI}
                <Switch
                    checked={provider == OPENAI}
                    onChange={handleProviderChange}
                    inputProps={{ 'aria-label': 'switch' }}
                />
                {OPENAI}
            </Typography>
            <Button
                variant='contained' style={{ width: '200px', alignSelf: 'center' }}
                onClick={handleImportExport}
            >
                Import/Export
            </Button>

            {
                importExport &&
                <TextField
                    multiline
                    value={JSON.stringify(params, undefined, 2)}
                    onChange={(e) => handleImportExportChange(e)}
                >

                </TextField>
            }
            <Stack direction={'row'} gap={5} margin={10}>
                <TextField multiline fullWidth required value={params?.domain} onChange={(e) => handleParamChange('domain', e)} id="outlined-basic1" label="Domain" variant="outlined" placeholder='E.g. Finance' />
                <TextField multiline fullWidth required value={params?.subdomain} onChange={(e) => handleParamChange('subdomain', e)} id="outlined-basic2" label="Subdomain" variant="outlined" placeholder='E.g. banking-operations' />
                <TextField multiline fullWidth value={params?.functions} onChange={(e) => handleParamChange('functions', e)} id="outlined-basic3" label="Functions Name" variant="outlined" placeholder='E.g. make_an_order' />
                <TextField multiline fullWidth value={params?.descriptions} required onChange={(e) => handleParamChange('descriptions', e)} id="outlined-basic4" label="Function Descriptions" variant="outlined" placeholder='E.g. The function should do buy or sell order based on some price limits, also might want to have a time limit where the order should be executed within some certain amount of duration e.g. minutes hours days' />
                <TextField multiline fullWidth value={params?.parameters} onChange={(e) => handleParamChange('parameters', e)} id="outlined-basic5" label="Function Parameters" variant="outlined" placeholder='E.g. amount, fromCurrency, toCurrency, time_limit_in_hours' />
                <TextField multiline fullWidth value={params?.prompt} onChange={(e) => handleParamChange('prompt', e)} id="outlined-basic5" label="Custom Prompt" variant="outlined" placeholder='E.g. Please now do your best by the requirements provided to you above!' />
            </Stack>

            <Stack direction={'row'} gap={5} margin={10}>
                <Button style={{ "cursor": "pointer" }} fullWidth onClick={handleGenerate} variant={isLoading ? 'contained' : 'outlined'}>Generate</Button>
                <Button
                    ref={downloadBtn.current}
                    href={fileHref}
                    download={notebookName}
                    variant='contained'
                    disabled={isGenerating}
                >
                    {`Download Notebook`}
                </Button>
            </Stack>

            {
                isLoading && <Alert severity='info'>Loading...</Alert>
            }
            <Stack direction={'row'} gap={5} margin={10}>
                <MuiMarkdown>
                    {responseText as string}
                </MuiMarkdown>
            </Stack>
        </Stack>

    );
}

export default Generator;
